import { OfflineManagerAction, OfflineManagerActionKeys } from './actions';
import { MultiAppAuthenticationInfosAction, MultiAppAuthenticationInfosActionKeys } from '../MultiAppAuthentication/actions';
import UserAgentUtils from '../../utils/userAgent';
import Utils from '../../utils/index';
import IndexedDBManager from '../../utils/indexedDB/manager';

export interface OfflineManagerState {
	status: OfflineManagerStatus;
	thumbMap: ThumbMap;
	requiredItems: { [k: number]: OfflineItemStatus };
	bgMediaMap: { [k: number]: OfflineItemStatus };
	brandThumb: LogoThumbState;
	homeThumb: LogoThumbState;
	defaultFolderIcon: DefaultFolderIconState;
	lastUpdateTimestamp?: Date;
	isDownloadingThumbs: boolean;
	isDownloadingRequiredItems: boolean;
	isPreparingContentStructure: boolean;
	contentStructureReady: boolean;
	contentStructureEverCompleted: boolean;
	isContentStructureToastClosed: boolean;
}

export type ThumbMap = { [k: number]: OfflineItemStatus };

interface LogoDownloadState {
	filename?: string;
	status: DownloadStatus;
}

export interface LogoThumbState {
	localConfig: LogoDownloadState;
	remoteConfig: LogoDownloadState;
}

export interface OfflineItemStatus {
	status: DownloadStatus;
	version: number;
	isIcon?: boolean;
	url?: string;
	fileName?: string;
}

export interface DefaultFolderIconState {
	status: DownloadStatus;
	name?: string;
}

export enum BrandThumbConfigLocation {
	LOCAL_CONFIG = 'localConfig',
	REMOTE_CONFIG = 'remoteConfig',
}

export enum ThumbType {
	BRAND_THUMB = 'brandThumb',
	HOME_THUMB = 'homeThumb'
}

export enum LogoType {
	BRAND_LOGO = 'logo',
	HOME_LOGO = 'logoHome'
}

export enum OfflineManagerStatus {
	NOT_INITIALIZED = 'NOT_INITIALIZED',
	INITIALIZING = 'INITIALIZING',
	INITIALIZED = 'INITIALIZED',
	ERROR = 'ERROR'
}

export enum DownloadStatus {
	NOT_DOWNLOADED = 'NOT_DOWNLOADED',
	DOWNLOADING = 'DOWNLOADING',
	DOWNLOADED = 'DOWNLOADED',
	ERROR = 'ERROR',
}

let initialState: OfflineManagerState = {
	status: OfflineManagerStatus.NOT_INITIALIZED,
	thumbMap: {},
	requiredItems: {},
	bgMediaMap: {},
	brandThumb: {
		localConfig: { status: DownloadStatus.NOT_DOWNLOADED },
		remoteConfig: { status: DownloadStatus.NOT_DOWNLOADED }
	},
	homeThumb: {
		localConfig: { status: DownloadStatus.NOT_DOWNLOADED },
		remoteConfig: { status: DownloadStatus.NOT_DOWNLOADED }
	},
	defaultFolderIcon: {
		status: DownloadStatus.NOT_DOWNLOADED,
	},
	isDownloadingThumbs: false,
	isDownloadingRequiredItems: false,
	isPreparingContentStructure: false,
	contentStructureReady: false,
	contentStructureEverCompleted: false,
	isContentStructureToastClosed: false
};

let initialItemState: OfflineItemStatus = {
	status: DownloadStatus.NOT_DOWNLOADED,
	version: -1
};

export function offlineManager(
	state: OfflineManagerState = initialState,
	action: OfflineManagerAction | MultiAppAuthenticationInfosAction
): OfflineManagerState {
	let newState = Utils.deepCopy(state);
	let newItem: OfflineItemStatus;
	let newOfflineManager: OfflineManagerState;

	switch (action.type) {
		case OfflineManagerActionKeys.INIT_THUMB_MAP:
			newOfflineManager = {
				...state,
				thumbMap: action.thumbMap,
			};
			break;
		case OfflineManagerActionKeys.INIT_REQUIRED_ITEMS:
			newOfflineManager = {
				...state,
				requiredItems: action.requiredItems,
			};
			break;
		case OfflineManagerActionKeys.INIT_BG_MEDIA_MAP:
			newOfflineManager = {
				...state,
				bgMediaMap: action.bgMediaMap,
			};
			break;
		case OfflineManagerActionKeys.INIT_LAST_UPDATE_TIMESTAMP:
			newOfflineManager = {
				...state,
				lastUpdateTimestamp: action.lastUpdateTimestamp,
			};
			break;
		case OfflineManagerActionKeys.START_INITIALIZATION:
			newOfflineManager = {
				...state,
				status: OfflineManagerStatus.INITIALIZING,
			};
			break;
		case OfflineManagerActionKeys.END_INITIALIZATION:
			newOfflineManager = {
				...state,
				status: OfflineManagerStatus.INITIALIZED,
				lastUpdateTimestamp: new Date()
			};
			break;
		case OfflineManagerActionKeys.INITIALIZATION_ERROR:
			newOfflineManager = {
				...state,
				status: OfflineManagerStatus.ERROR
			};
			break;
		case OfflineManagerActionKeys.UPDATE_THUMB_STATUS:
			newItem = getItemFromMap(newState.thumbMap, action.driveItemId);
			newItem.status = action.status;
			newItem.version = action.version;
			newItem.fileName = action.fileName;
			newState.thumbMap[action.driveItemId] = newItem;
			newOfflineManager = newState;
			break;

		case OfflineManagerActionKeys.UPDATE_REQUIRED_ITEM_STATUS:
			newItem = getItemFromMap(newState.requiredItems, action.id);
			newItem.status = action.status;
			newItem.version = action.version;
			newItem.url = action.url;
			newItem.isIcon = action.isIcon;
			newState.requiredItems[action.id] = newItem;
			newOfflineManager = newState;
			break;

		case OfflineManagerActionKeys.UPDATE_FOLDER_BG_MEDIA_STATUS:
			newItem = getItemFromMap(newState.bgMediaMap, action.driveItemId);
			newItem.status = action.status;
			newItem.version = action.version;
			newState.bgMediaMap[action.driveItemId] = newItem;
			newOfflineManager = newState;
			break;

		case OfflineManagerActionKeys.REMOVE_REQUIRED_ITEM:
			delete newState.requiredItems[action.id];
			newOfflineManager = newState;
			break;
		case OfflineManagerActionKeys.REMOVE_THUMB:
			delete newState.thumbMap[action.id];
			newOfflineManager = newState;
			break;
		case OfflineManagerActionKeys.REMOVE_FOLDER_BG_MEDIA:
			delete newState.bgMediaMap[action.id];
			newOfflineManager = newState;
			break;
		case OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS:
			newOfflineManager = {
				...state,
				isDownloadingRequiredItems: action.isDownloading,
			};
			break;
		case OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS:
			newOfflineManager = {
				...state,
				isDownloadingThumbs: action.isDownloading,
			};
			break;
		case OfflineManagerActionKeys.INIT_BRAND_THUMB:
			return {
				...state,
				brandThumb: action.brandThumb
			};
		case OfflineManagerActionKeys.INIT_HOME_THUMB:
			return {
				...state,
				homeThumb: action.homeThumb
			};
		case OfflineManagerActionKeys.INIT_DEFAULT_FOLDER_ICON:
			return {
				...state,
				defaultFolderIcon: action.defaultFolderIcon,
			};
		case OfflineManagerActionKeys.UPDATE_BRAND_THUMB:
			newState[action.thumbType][action.configLocation] = {
				status: action.status,
				filename: action.filename
			};
			return newState;
		case OfflineManagerActionKeys.UPDATE_DEFAULT_FOLDER_ICON:
			newState.defaultFolderIcon = {
				status: action.status,
				name: action.name,
			};
			return newState;
		case OfflineManagerActionKeys.START_CONTENT_STRUCTURE_PREPARATION:
			newOfflineManager = {
				...state,
				isPreparingContentStructure: true,
				isContentStructureToastClosed: false, // Reset closed flag when preparation starts
			};
			break;
		case OfflineManagerActionKeys.END_CONTENT_STRUCTURE_PREPARATION:
			newOfflineManager = {
				...state,
				isPreparingContentStructure: false,
				contentStructureReady: true,
			};
			break;
		case OfflineManagerActionKeys.CLEAR_CONTENT_STRUCTURE_READY:
			newOfflineManager = {
				...state,
				contentStructureReady: false,
			};
			break;
		case OfflineManagerActionKeys.MARK_CONTENT_STRUCTURE_COMPLETED:
			newOfflineManager = {
				...state,
				contentStructureEverCompleted: true,
			};
			break;
		case OfflineManagerActionKeys.CLOSE_CONTENT_STRUCTURE_TOAST:
			newOfflineManager = {
				...state,
				isContentStructureToastClosed: true,
			};
			break;
		case MultiAppAuthenticationInfosActionKeys.LOGOUT:
			newOfflineManager = {
				...state,
				status: OfflineManagerStatus.NOT_INITIALIZED,
				isPreparingContentStructure: false,
				isContentStructureToastClosed: false,
			};
			break;
		default:
			newOfflineManager = state;
			break;
	}

	if (Utils.stringIsInStringEnum(action.type, OfflineManagerActionKeys)) {
		if (UserAgentUtils.isOnline() && UserAgentUtils.isMobileApp) {
			IndexedDBManager.setStoreOfflineManager(newOfflineManager).then();
		}
	}

	return newOfflineManager;
}

function getItemFromMap(items: { [k: number]: OfflineItemStatus }, itemId: number): OfflineItemStatus {
	return items[itemId] ? items[itemId] : Utils.deepCopy(initialItemState);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/OfflineManager/reducer.ts